import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Alert, Modal, ButtonGroup } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { el } from 'date-fns/locale';
import { io } from 'socket.io-client';
import TemperatureChart from './Chart';
import api from '../api';

registerLocale('el', el);

function CustomTimeInput({ date, onChange }) {
  const d = date ? new Date(date) : new Date();
  const currentHour = d.getHours();
  const currentMinute = d.getMinutes();

  const handleHour = (e) => {
    const h = e.target.value;
    const minStr = String(currentMinute).padStart(2, '0');
    onChange(`${String(h).padStart(2, '0')}:${minStr}`);
  };

  const handleMinute = (e) => {
    const m = e.target.value;
    const hourStr = String(currentHour).padStart(2, '0');
    onChange(`${hourStr}:${String(m).padStart(2, '0')}`);
  };

  return (
    <div style={{display:'flex', gap:8, alignItems:'center'}}>
      <select aria-label="hour" value={currentHour} onChange={handleHour} className="form-select form-select-sm">
        {Array.from({length: 24}).map((_, i) => (
          <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
        ))}
      </select>
      <span className="fw-bold">:</span>
      <select aria-label="minute" value={currentMinute} onChange={handleMinute} className="form-select form-select-sm">
        {Array.from({length: 60}).map((_, i) => (
          <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
        ))}
      </select>
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || '';

// Custom CSS με υποστήριξη Dark Mode
const styles = `
  /* Ομαλή μετάβαση χρωμάτων για το Dark Mode */
  * { transition: background-color 0.3s ease, border-color 0.3s ease; }

  /* Status Dots */
  .status-dot { height: 10px; width: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
  .status-online { background-color: #34d399; box-shadow: 0 0 8px rgba(52,211,153,0.6); }
  .status-offline { background-color: #ef4444; }

  /* ΚΟΚΚΙΝΟ FLASH ΣΥΝΑΓΕΡΜΟΥ */
  @keyframes blink-red {
    0% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.7); border-color: #f87171; }
    70% { box-shadow: 0 0 15px 15px rgba(248, 113, 113, 0); border-color: #f87171; }
    100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0); border-color: #f87171; }
  }

  /* --- GRID VIEW --- */
  .hover-card {
    position: relative;
    height: 135px;
    background-color: #ffffff;
    border: 1px solid #cbd5e1;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
    border-radius: 1rem;
    display: flex;
    flex-direction: column;
    padding: 12px;
    cursor: pointer;
  }
  .hover-card:hover {
    border-color: #94a3b8;
    box-shadow: 0 10px 20px -5px rgba(0,0,0,0.15);
  }
  .hover-card.selected { border-color: #3b82f6; background-color: #eff6ff; }
  .hover-card.alarm-active { 
    animation: blink-red 1.5s infinite; 
    border-color: #f87171 !important; 
  }

  /* TOOLTIP (Popup) */
  .card-tooltip {
    position: absolute;
    z-index: 50;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(10px);
    margin-bottom: 12px;
    width: 210px;
    background-color: #1e293b;
    color: #ffffff;
    border-radius: 0.75rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    padding: 12px;
    transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
  }
  .hover-card:hover .card-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
  .card-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px;
    border-style: solid;
    border-color: #1e293b transparent transparent transparent;
  }

  /* --- LIST VIEW --- */
  .hover-row {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #f8fafc;
    background-color: #ffffff;
    cursor: pointer;
  }
  .hover-row:hover { background-color: #f8fafc; }
  .hover-row.selected { background-color: #eff6ff; border-color: #bfdbfe; }
  .hover-row.alarm { background-color: #fef2f2; }

  .col-name { width: 35%; display: flex; align-items: center; gap: 10px; flex-shrink: 0; padding-right: 15px;}
  .col-temp { width: 15%; display: flex; justify-content: flex-start; flex-shrink: 0; }
  .col-door { width: 15%; display: flex; justify-content: flex-start; flex-shrink: 0; }
  .col-stats { width: 35%; display: flex; justify-content: flex-end; align-items: center; flex-shrink: 0; }

  /* Σταθερό Pill Θερμοκρασίας */
  .temp-pill {
    display: inline-block;
    width: 90px;
    text-align: center;
    padding: 6px 0;
    border-radius: 999px;
    font-size: 1rem;
    font-weight: 700;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
  .temp-pill.success { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .temp-pill.danger { background-color: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

  .door-pill-success { background-color: #ecfdf5; color: #059669; padding: 4px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 700; border: 1px solid #a7f3d0;}
  .door-pill-danger { background-color: #fef2f2; color: #dc2626; padding: 4px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 700; border: 1px solid #fecaca;}
  
  .icon-badge-success { background-color: #d1fae5; color: #059669; padding: 2px 6px; border-radius: 6px; }
  .icon-badge-danger { background-color: #fee2e2; color: #dc2626; padding: 2px 6px; border-radius: 6px; }

  /* =========================================
     DARK MODE OVERRIDES
     ========================================= */
  .dark-mode { background-color: #0f172a !important; color: #f8fafc !important; min-height: 100vh; }
  .dark-mode .bg-white { background-color: #1e293b !important; border-color: #334155 !important; }
  .dark-mode .bg-light { background-color: #334155 !important; border-color: #475569 !important; }
  .dark-mode .text-dark { color: #f1f5f9 !important; }
  .dark-mode .text-muted { color: #94a3b8 !important; }
  .dark-mode .border-light { border-color: #334155 !important; }
  .dark-mode .border-bottom { border-color: #334155 !important; }
  
  .dark-mode .hover-card { background-color: #1e293b; border-color: #334155; }
  .dark-mode .hover-card:hover { border-color: #64748b; }
  .dark-mode .hover-card.selected { border-color: #3b82f6; background-color: rgba(59, 130, 246, 0.1); }
  
  .dark-mode .hover-row { background-color: #1e293b; border-bottom-color: #334155; }
  .dark-mode .hover-row:hover { background-color: #334155; }
  .dark-mode .hover-row.selected { background-color: rgba(59, 130, 246, 0.1); border-color: #3b82f6; }
  
  .dark-mode .card-tooltip { background-color: #0f172a; border: 1px solid #334155; }
  .dark-mode .card-tooltip::after { border-top-color: #0f172a; }
  
  .dark-mode .form-control, .dark-mode .form-select { background-color: #0f172a; border-color: #475569; color: #f8fafc; }
  .dark-mode .form-select option { background-color: #0f172a; color: #f8fafc; }
  
  .dark-mode .modal-content { background-color: #1e293b; border-color: #475569; color: #f8fafc; }
  .dark-mode .modal-header, .dark-mode .modal-footer { border-color: #334155; }
  .dark-mode .btn-close { filter: invert(1) grayscale(100%) brightness(200%); }

  /* Προσαρμογή Χρωμάτων για τα Pills στο Dark Mode */
  .dark-mode .temp-pill.success { background-color: rgba(5, 150, 105, 0.2); color: #34d399; border-color: rgba(52, 211, 153, 0.3); }
  .dark-mode .temp-pill.danger { background-color: rgba(220, 38, 38, 0.2); color: #f87171; border-color: rgba(248, 113, 113, 0.3); }
  .dark-mode .door-pill-success { background-color: rgba(5, 150, 105, 0.2); color: #34d399; border-color: rgba(52, 211, 153, 0.3); }
  .dark-mode .door-pill-danger { background-color: rgba(220, 38, 38, 0.2); color: #f87171; border-color: rgba(248, 113, 113, 0.3); }
  .dark-mode .icon-badge-success { background-color: rgba(5, 150, 105, 0.2); color: #34d399; }
  .dark-mode .icon-badge-danger { background-color: rgba(220, 38, 38, 0.2); color: #f87171; }
`;

function Dashboard({ user }) {
  const [viewMode, setViewMode] = useState('grid'); 
  const [deviceFilter, setDeviceFilter] = useState('all'); 
  
  // STATE ΓΙΑ DARK MODE (Αποθήκευση στο Local Storage για να το θυμάται)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('dashboard_theme') === 'dark';
  });

  // Εφαρμογή Dark Mode στο Body 
  useEffect(() => {
    localStorage.setItem('dashboard_theme', isDarkMode ? 'dark' : 'light');
    document.body.style.backgroundColor = isDarkMode ? '#0f172a' : '#f8fafc';
  }, [isDarkMode]);

  const getStatus = (lastUpdate) => {
    if (!lastUpdate) return { class: 'status-offline', text: 'No Data' };
    const now = new Date();
    const last = new Date(lastUpdate);
    const diffInMinutes = (now - last) / 60000;
    return diffInMinutes < 10 ? { class: 'status-online', text: 'Online' } : { class: 'status-offline', text: 'Offline' };
  };
  
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  
  const [timeRange, setTimeRange] = useState(24);
  const isCustom = timeRange === 'custom';
  
  const [startDate, setStartDate] = useState(null); 
  const [endDate, setEndDate] = useState(null);     
  const [liveData, setLiveData] = useState({});
  const [statistics, setStatistics] = useState(null);
  const [, setSocket] = useState(null); 
  const [error, setError] = useState('');
  
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDeviceId, setExportDeviceId] = useState('selected');
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await api.get('/api/dashboard/devices');
      const devicesData = response.data.data;
      setDevices(devicesData);
      const initialLiveData = {}; 

      devicesData.forEach(device => {
        if (device.lastTemperature !== null) {
          initialLiveData[device.id] = {
            temperature: device.lastTemperature,
            humidity: device.lastHumidity,
            doorOpen: device.lastDoorOpen,
            timestamp: device.lastUpdate,
            minLimit: device.DeviceAlert?.minTemp ?? 0,
            maxLimit: device.DeviceAlert?.maxTemp ?? 20,
            stats24h: device.stats24h
          };
        }
      });

      setLiveData(initialLiveData);
      
      const uniqueLocations = [...new Set(devicesData.map(d => ({
        id: d.locationId,
        name: d.locationName
      })))].filter((v, i, a) => 
        a.findIndex(t => t.id === v.id) === i
      );
      setLocations(uniqueLocations);
      
    } catch (err) {
      setError('Σφάλμα κατά τη φόρτωση συσκευών');
      console.error(err);
    }
  }, []); 

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await api.get('/api/dashboard/statistics');
      setStatistics(response.data.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, []);

  useEffect(() => {
    const newSocket = io(API_URL);
    setSocket(newSocket);

    newSocket.on('temperature_update', (data) => {
      setLiveData(prev => ({
        ...prev,
        [data.deviceId]: {
          ...prev[data.deviceId],
          temperature: data.temperature,
          humidity: data.humidity,
          doorOpen: data.doorOpen,
          timestamp: data.timestamp
        }
      }));
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === data.deviceId 
            ? { ...d, lastTemperature: data.temperature, lastDoorOpen: data.doorOpen, lastUpdate: data.timestamp } 
            : d
        )
      );
    });

    newSocket.on('alert', (data) => {
      setLiveData(prev => ({
        ...prev,
        [data.deviceId]: {
          ...prev[data.deviceId],
          lastAlert: data.type,
          temperature: data.temperature ?? prev[data.deviceId]?.temperature,
          humidity: data.humidity ?? prev[data.deviceId]?.humidity,
          timestamp: data.timestamp ?? prev[data.deviceId]?.timestamp
        }
      }));
    });

    return () => newSocket.close();
  }, []); 

  useEffect(() => {
    fetchDevices();
    fetchStatistics();
    return () => {};
  }, [fetchDevices, fetchStatistics]);

  useEffect(() => {
    if (isCustom && !startDate && !endDate) {
      const now = new Date();
      const past = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      setStartDate(past);
      setEndDate(now);
    }
  }, [isCustom, startDate, endDate]);

  const handleDeviceToggle = (deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) return prev.filter(id => id !== deviceId);
      return [...prev, deviceId];
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredDevices();
    setSelectedDevices(filtered.map(d => d.id));
  };

  const handleClearAll = () => setSelectedDevices([]);

  const setQuickRange = (hours) => {
    const now = new Date();
    const past = new Date(now.getTime() - hours * 60 * 60 * 1000);
    setExportEndDate(now);
    setExportStartDate(past);
  };

  const handleExportWithDates = async (format) => {
    try {
      const deviceIdsParam = exportDeviceId === 'selected' ? selectedDevices.join(',') : exportDeviceId;
      let url = `/api/exports/${format}?deviceIds=${deviceIdsParam}`;
      if (exportStartDate && exportEndDate) {
        const s = exportStartDate instanceof Date ? exportStartDate.toISOString() : exportStartDate;
        const e = exportEndDate instanceof Date ? exportEndDate.toISOString() : exportEndDate;
        url += `&startDate=${encodeURIComponent(s)}&endDate=${encodeURIComponent(e)}`;
      } else {
        const exportHours = isCustom ? 24 : timeRange; 
        url += `&hours=${exportHours}`;
      }
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `report_${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
          fileName = decodeURIComponent(contentDisposition.split('filename=')[1].replace(/"/g, ''));
      }
      link.download = fileName;
      link.click();
      setShowExportModal(false);
    } catch (err) {
      setError(`Σφάλμα κατά την εξαγωγή ${format.toUpperCase()}`);
    }
  };

  const isAnyDoorOpen = (doorStatus) => {
    if (!doorStatus) return false;
    if (typeof doorStatus === 'object') return Object.values(doorStatus).some(val => val === true);
    return doorStatus === true;
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'freezer': return 'bi-snow text-info';
      case 'room': return 'bi-house text-secondary';
      case 'fridge': return 'bi-thermometer-half text-primary';
      default: return 'bi-thermometer-half text-secondary';
    }
  };

  const getFilteredDevices = () => {
    let filtered = selectedLocation === 'all' ? devices : devices.filter(d => d.locationId === parseInt(selectedLocation));
    
    if (deviceFilter === 'freezer') {
      filtered = filtered.filter(d => d.deviceType === 'freezer');
    } else if (deviceFilter === 'room') {
      filtered = filtered.filter(d => d.deviceType === 'room');
    } else if (deviceFilter === 'fridge') {
      filtered = filtered.filter(d => d.deviceType === 'fridge' || (!['freezer', 'room'].includes(d.deviceType)));
    } else if (deviceFilter === 'alarms') {
      filtered = filtered.filter(d => {
        const data = liveData[d.id] || {};
        const temp = parseFloat(data.temperature ?? d.lastTemperature ?? 0);
        const minLimit = parseFloat(data.minLimit ?? d.DeviceAlert?.minTemp ?? 0);
        const maxLimit = parseFloat(data.maxLimit ?? d.DeviceAlert?.maxTemp ?? 20);
        const doorStatus = data.doorOpen ?? d.lastDoorOpen;
        const alertsActive = d.DeviceAlert?.alertsEnabled ?? false;
        
        const isTempAlarm = alertsActive && (temp > maxLimit || temp < minLimit);
        const isDoorAlarm = isAnyDoorOpen(doorStatus);
        return isTempAlarm || isDoorAlarm;
      });
    }
    return filtered;
  };

  const filteredDevices = getFilteredDevices();
  const devicesByLocation = filteredDevices.reduce((acc, device) => {
    const loc = device.locationName;
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(device);
    return acc;
  }, {});

  const renderDoorIconMinimal = (status) => {
    if (status === null || status === undefined || status === false || status === 0) return null;
    if (typeof status === 'object' && Object.keys(status).length > 0) {
        const keys = Object.keys(status).sort();
        return (
            <div className="d-flex gap-1 align-items-center">
                {keys.map((key, index) => {
                    const isOpen = status[key] === true || status[key] === "true" || status[key] === 1;
                    return (
                      <div key={key} className={isOpen ? 'icon-badge-danger' : 'icon-badge-success'} title={`Πόρτα ${index + 1}: ${isOpen ? 'Ανοιχτή' : 'Κλειστή'}`}>
                        <i className={`bi ${isOpen ? 'bi-door-open-fill' : 'bi-door-closed'}`} style={{fontSize: '0.9rem'}}></i>
                      </div>
                    );
                })}
            </div>
        );
    }
    return (
      <div className={status ? 'icon-badge-danger' : 'icon-badge-success'} title={status ? 'ΑΝΟΙΧΤΗ' : 'Κλειστή'}>
        <i className={`bi ${status ? 'bi-door-open-fill' : 'bi-door-closed'}`} style={{fontSize: '0.9rem'}}></i>
      </div>
    );
  };

  const renderDoorBadgeCompact = (status) => {
    if (status === null || status === undefined || status === false || status === 0) return null;
    if (typeof status === 'object' && Object.keys(status).length > 0) {
        const keys = Object.keys(status).sort();
        return (
            <div className="d-flex gap-1 justify-content-center">
                {keys.map((key, index) => {
                    const isOpen = status[key] === true || status[key] === "true" || status[key] === 1;
                    return (
                        <div key={key} className={isOpen ? "door-pill-danger" : "door-pill-success"}>
                            {index + 1}: <i className={`bi ${isOpen ? 'bi-door-open-fill' : 'bi-door-closed'} ms-1`}></i>
                        </div>
                    );
                })}
            </div>
        );
    }
    return (
        <div className={status ? "door-pill-danger d-inline-flex align-items-center" : "door-pill-success d-inline-flex align-items-center"}>
            <i className={`bi ${status ? 'bi-door-open-fill' : 'bi-door-closed'} me-1`}></i>
            {status ? 'Ανοιχτή' : 'Κλειστή'}
        </div>
    );
  };

  return (
    <div className={isDarkMode ? 'dark-mode' : ''} style={{ minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
      <Container fluid className="px-md-4 py-3">
        <style>{styles}</style>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        
        {/* HEADER, TOGGLES & DARK MODE */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-white p-3 rounded-4 shadow-sm border border-light mb-4">
          <div>
            <h4 className="mb-0 fw-bold text-dark d-flex align-items-center">
              <i className="bi bi-speedometer2 text-primary me-2"></i>Dashboard
            </h4>
            {user && user.role === 'super_admin' && <p className="text-muted mb-0 small mt-1">{user.clientName}</p>}
          </div>
          <div className="d-flex align-items-center mt-3 mt-md-0">
            {/* View Mode Toggle */}
            <div className="bg-light p-1 rounded-3 d-inline-flex">
              <Button 
                variant={viewMode === 'grid' ? 'white' : 'light'} 
                className={`border-0 rounded-2 px-3 py-1 ${viewMode === 'grid' ? 'text-primary fw-bold shadow-sm' : 'text-muted'}`}
                onClick={() => setViewMode('grid')}
              >
                <i className="bi bi-grid-fill me-2"></i>Κάρτες
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'white' : 'light'} 
                className={`border-0 rounded-2 px-3 py-1 ${viewMode === 'list' ? 'text-primary fw-bold shadow-sm' : 'text-muted'}`}
                onClick={() => setViewMode('list')}
              >
                <i className="bi bi-list-ul me-2"></i>Λίστα
              </Button>
            </div>
            
            {/* Dark Mode Button */}
            <Button 
              variant={isDarkMode ? 'dark' : 'outline-secondary'} 
              className="border-0 rounded-circle ms-3 shadow-sm d-flex align-items-center justify-content-center"
              style={{ width: '38px', height: '38px', backgroundColor: isDarkMode ? '#1e293b' : '#ffffff' }}
              onClick={() => setIsDarkMode(!isDarkMode)}
              title="Εναλλαγή Dark Mode"
            >
              {isDarkMode ? <i className="bi bi-moon-stars-fill text-warning"></i> : <i className="bi bi-sun-fill text-warning"></i>}
            </Button>
          </div>
        </div>

        {user && user.role === 'super_admin' && statistics && (
          <Row className="mb-4">
            <Col xs={6} md={3}><Card className="text-center border-0 shadow-sm rounded-4 mb-3 bg-white"><Card.Body><i className="bi bi-building fs-1 text-primary"></i><h3 className="mt-2 mb-0 fw-bold text-dark">{statistics.totalClients}</h3><small className="text-muted">Clients</small></Card.Body></Card></Col>
            <Col xs={6} md={3}><Card className="text-center border-0 shadow-sm rounded-4 mb-3 bg-white"><Card.Body><i className="bi bi-geo-alt fs-1 text-info"></i><h3 className="mt-2 mb-0 fw-bold text-dark">{statistics.totalLocations}</h3><small className="text-muted">Τοποθεσίες</small></Card.Body></Card></Col>
            <Col xs={6} md={3}><Card className="text-center border-0 shadow-sm rounded-4 mb-3 bg-white"><Card.Body><i className="bi bi-thermometer fs-1 text-success"></i><h3 className="mt-2 mb-0 fw-bold text-dark">{statistics.totalDevices}</h3><small className="text-muted">Συσκευές</small></Card.Body></Card></Col>
            <Col xs={6} md={3}><Card className="text-center border-0 shadow-sm rounded-4 mb-3 bg-white"><Card.Body><i className="bi bi-graph-up fs-1 text-warning"></i><h3 className="mt-2 mb-0 fw-bold text-dark">{statistics.readings24h}</h3><small className="text-muted">Μετρήσεις (24h)</small></Card.Body></Card></Col>
          </Row>
        )}

        {/* ΝΕΑ ΜΠΑΡΑ ΦΙΛΤΡΩΝ */}
        <div className="d-flex flex-column flex-md-row gap-3 mb-4 bg-white p-3 rounded-4 shadow-sm border border-light">
          {locations.length > 1 && (
            <Form.Group className="flex-grow-1" style={{maxWidth: '300px'}}>
              <Form.Label className="fw-bold text-muted small mb-1" style={{fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Φίλτρο Τοποθεσίας</Form.Label>
              <Form.Select 
                className="border-light shadow-sm rounded-3 fw-semibold text-dark"
                value={selectedLocation} 
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                }}
              >
                <option value="all">Όλες οι Τοποθεσίες</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="flex-grow-1" style={{maxWidth: '300px'}}>
            <Form.Label className="fw-bold text-muted small mb-1" style={{fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Κατηγορία / Κατάσταση</Form.Label>
            <Form.Select 
              className="border-light shadow-sm rounded-3 fw-semibold text-dark"
              value={deviceFilter} 
              onChange={(e) => setDeviceFilter(e.target.value)}
            >
              <option value="all">Όλες οι Συσκευές</option>
              <option value="freezer">❄️ Καταψύκτες</option>
              <option value="fridge">🌡️ Ψυγεία / Συντηρήσεις</option>
              <option value="room">🏠 Χώροι</option>
              <option value="alarms">🚨 Μόνο Συναγερμοί</option>
            </Form.Select>
          </Form.Group>
        </div>

        {/* LOOP ΣΥΣΚΕΥΩΝ ΑΝΑ ΤΟΠΟΘΕΣΙΑ */}
        {Object.keys(devicesByLocation).length === 0 ? (
          <Alert variant="secondary" className="rounded-4 border-0 shadow-sm p-4 text-center text-muted">
            Δεν βρέθηκαν συσκευές με αυτά τα κριτήρια.
          </Alert>
        ) : (
          Object.keys(devicesByLocation).map(locationName => (
            <div key={locationName} className="mb-5">
              <h5 className="fw-bold text-dark mb-3 d-flex align-items-center">
                <i className="bi bi-geo-alt-fill text-primary me-2"></i>{locationName}
                <span className="ms-2 badge bg-light text-secondary rounded-pill border">{devicesByLocation[locationName].length}</span>
              </h5>
              
              {viewMode === 'list' ? (
                /* =========================================================
                   ΠΡΟΒΟΛΗ 2: LIST VIEW
                   ========================================================= */
                <div className="bg-white rounded-4 shadow-sm border border-light" style={{overflowX: 'auto'}}>
                  <div style={{minWidth: '850px'}}>
                    {/* Header Λίστας */}
                    <div className="d-flex px-3 py-3 bg-light border-bottom text-muted text-uppercase fw-bold" style={{fontSize: '0.75rem', letterSpacing: '0.5px'}}>
                      <div className="col-name ps-2">Ονομα Συσκευης</div>
                      <div className="col-temp">Θερμοκρασια</div>
                      <div className="col-door">Πορτα</div>
                      <div className="col-stats pe-3">Στατιστικα (24h) & Ενημερωση</div>
                    </div>

                    {/* Γραμμές Λίστας */}
                    <div>
                      {devicesByLocation[locationName].map(device => {
                        const data = liveData[device.id] || {};
                        const alertsActive = device.DeviceAlert?.alertsEnabled ?? false;
                        const temp = parseFloat(data.temperature ?? device.lastTemperature ?? 0);
                        const minLimit = parseFloat(data.minLimit ?? device.DeviceAlert?.minTemp ?? 0);
                        const maxLimit = parseFloat(data.maxLimit ?? device.DeviceAlert?.maxTemp ?? 20);
                        const doorStatus = data.doorOpen ?? device.lastDoorOpen;
                        const stats = data.stats24h || { min: '--', max: '--', avg: '--' };

                        const isTempAlarm = alertsActive && (temp > maxLimit || temp < minLimit);
                        const isSelected = selectedDevices.includes(device.id);
                        const statusInfo = getStatus(device.lastUpdate);

                        return (
                          <div 
                            key={device.id} 
                            onClick={() => handleDeviceToggle(device.id)}
                            className={`hover-row ${isSelected ? 'selected' : ''} ${(isTempAlarm || isAnyDoorOpen(doorStatus)) ? 'alarm' : ''}`}
                          >
                            <div className="col-name">
                              <span className={`status-dot ${statusInfo.class} ms-2`}></span>
                              <span className="fw-bold text-dark text-truncate d-inline-block" style={{maxWidth: '220px', fontSize: '0.95rem'}} title={device.name}>
                                {device.name}
                              </span>
                              {isSelected && <i className="bi bi-check-circle-fill text-primary ms-1"></i>}
                            </div>

                            <div className="col-temp">
                               <span className={`temp-pill ${isTempAlarm ? 'danger' : 'success'}`}>
                                 {temp?.toFixed(1) || '--'}°C
                               </span>
                            </div>

                            <div className="col-door">
                              {renderDoorBadgeCompact(doorStatus)}
                            </div>

                            <div className="col-stats">
                              <div className="row-stats d-flex align-items-center w-100 justify-content-end">
                                <div className="text-muted d-flex align-items-center me-4" style={{fontSize: '0.85rem', fontWeight: '500'}}>
                                  <span title="Minimum">Min: <span className="text-dark fw-bold me-2">{stats.min}°</span></span>
                                  <span className="text-light me-2">|</span>
                                  <span title="Average">Avg: <span className="text-dark fw-bold me-2">{stats.avg}°</span></span>
                                  <span className="text-light me-2">|</span>
                                  <span title="Maximum">Max: <span className="text-dark fw-bold">{stats.max}°</span></span>
                                </div>
                                <div className="border-start ps-3 text-muted fw-medium" style={{fontSize: '0.85rem'}}>
                                  {data.timestamp ? new Date(data.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' }) : '---'}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* =========================================================
                   ΠΡΟΒΟΛΗ 1: GRID VIEW (Μικρές Κάρτες, Popup, Όνομα Κάτω)
                   ========================================================= */
                <Row>
                {devicesByLocation[locationName].map(device => {
                    const data = liveData[device.id] || {};
                    const alertsActive = device.DeviceAlert?.alertsEnabled ?? false;
                    const temp = parseFloat(data.temperature ?? device.lastTemperature ?? 0);
                    const minLimit = parseFloat(data.minLimit ?? device.DeviceAlert?.minTemp ?? 0);
                    const maxLimit = parseFloat(data.maxLimit ?? device.DeviceAlert?.maxTemp ?? 20);
                    const doorStatus = data.doorOpen ?? device.lastDoorOpen;
                    const stats = data.stats24h || { min: '--', max: '--', avg: '--' };

                    const isTempAlarm = alertsActive && (temp > maxLimit || temp < minLimit);
                    const isSelected = selectedDevices.includes(device.id);
                    const statusInfo = getStatus(device.lastUpdate);

                    return (
                      <Col key={device.id} xs={6} sm={4} md={4} lg={3} xl={2} className="mb-3">
                        <div 
                          className={`hover-card ${isSelected ? 'selected' : ''} ${(isTempAlarm || isAnyDoorOpen(doorStatus)) ? 'alarm-active' : ''}`}
                          onClick={() => handleDeviceToggle(device.id)}
                        >
                          
                          {/* ΤΟ ΜΑΓΙΚΟ POPUP (TOOLTIP) */}
                          <div className="card-tooltip">
                            <div className="fw-bold text-center border-bottom pb-2 mb-2 text-truncate" style={{borderColor: '#475569'}}>
                              {device.name}
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span style={{color: '#94a3b8'}}>Ελάχιστη (24h):</span>
                              <span className="fw-bold text-info">{stats.min}°C</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span style={{color: '#94a3b8'}}>Μέση (24h):</span>
                              <span className="fw-bold text-success">{stats.avg}°C</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span style={{color: '#94a3b8'}}>Μέγιστη (24h):</span>
                              <span className="fw-bold text-danger">{stats.max}°C</span>
                            </div>
                            <div className="text-center pt-2 mt-1 border-top" style={{fontSize: '0.65rem', color: '#94a3b8', borderColor: '#475569'}}>
                              Ενημερώθηκε: {data.timestamp ? new Date(data.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' }) : '---'}
                            </div>
                          </div>

                          {/* HEADER: Dot + Check (Αριστερά), Πόρτα (Δεξιά) */}
                          <div className="d-flex justify-content-between align-items-start w-100">
                            <div className="d-flex align-items-center gap-2">
                              <span className={`status-dot ${statusInfo.class}`}></span>
                              {isSelected && <i className="bi bi-check-circle-fill text-primary"></i>}
                            </div>
                            <div className="d-flex gap-1 align-items-center flex-shrink-0">
                              {renderDoorIconMinimal(doorStatus)}
                            </div>
                          </div>

                          {/* ΚΕΝΤΡΙΚΗ ΘΕΡΜΟΚΡΑΣΙΑ (Κεντραρισμένη στον διαθέσιμο χώρο) */}
                          <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
                            <div className={`fw-bolder lh-1 ${isTempAlarm ? 'text-danger' : 'text-success'}`} style={{fontSize: '2rem', letterSpacing: '-1.5px'}}>
                              {temp?.toFixed(1) || '--'}°
                            </div>
                          </div>

                          {/* ΟΝΟΜΑ (Καρφωμένο στο κάτω μέρος) */}
                          {/* 🛠️ ΡΥΘΜΙΣΕΙΣ ΚΕΝΩΝ: 
                              1. Το 'mt-auto' σπρώχνει το όνομα τέρμα κάτω. 
                              2. Αν θες να το ανεβάσεις λίγο πιο πάνω (μακριά από το πάτωμα της κάρτας), 
                                 άλλαξε το 'mb-0' σε 'mb-1' ή 'mb-2' (margin-bottom). 
                              3. Αν θες η θερμοκρασία να κατέβει πιο κοντά του, αφαίρεσε το 'mt-auto' και βάλε 'mt-2' κτλ. */}
                          <div className="fw-bold text-dark text-center px-1 w-100 mt-auto mb-2" style={{fontSize: '0.75rem', lineHeight: '1.2', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>
                            {device.name}
                          </div>

                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>
          ))
        )}

        {/* ΓΡΑΦΗΜΑ ΚΑΙ ΦΙΛΤΡΑ */}
        {devices.length > 0 && (
          <>
            <Card className="mb-4 shadow-sm border-0 bg-white rounded-4">
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={4} className="mb-3 mb-md-0">
                    <Form.Label className="fw-bold small text-muted">Επιλεγμένες ({selectedDevices.length})</Form.Label>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={handleSelectAll} className="rounded-3 px-3">Επιλογή Όλων</Button>
                      <Button size="sm" variant="outline-secondary" onClick={handleClearAll} className="rounded-3 px-3">Καθαρισμός</Button>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3 mb-md-0">
                    <Form.Label className="fw-bold small text-muted">Χρονικό Εύρος Γραφήματος</Form.Label>
                    <Form.Select 
                      className="rounded-3 border-light shadow-sm bg-white text-dark"
                      value={timeRange} 
                      onChange={(e) => {
                        const val = e.target.value;
                        setTimeRange(val === 'custom' ? 'custom' : Number(val));
                      }}
                    >
                      <option value={1}>Τελευταία 1 ώρα</option>
                      <option value={6}>Τελευταίες 6 ώρες</option>
                      <option value={24}>Τελευταίες 24 ώρες</option>
                      <option value={48}>Τελευταίες 48 ώρες</option>
                      <option value={168}>Τελευταίες 7 ημέρες</option>
                      <option value="custom">Προσαρμοσμένο Εύρος...</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="d-none d-md-block">&nbsp;</Form.Label>
                    <Button variant="primary" onClick={() => setShowExportModal(true)} disabled={selectedDevices.length === 0} className="w-100 rounded-3 shadow-sm">
                      <i className="bi bi-download me-2"></i>Εξαγωγή Δεδομένων
                    </Button>
                  </Col>
                </Row>

                {isCustom && (
                  <div className="mt-4 p-4 bg-light rounded-4 border border-light-subtle">
                    <Row>
                      <Col md={6} className="mb-3 mb-md-0">
                        <Form.Group>
                          <Form.Label className="small fw-semibold text-secondary mb-2">Από Ημερομηνία & Ώρα</Form.Label>
                          <DatePicker
                            selected={startDate}
                            onChange={(d) => setStartDate(d)}
                            showTimeInput
                            customTimeInput={<CustomTimeInput />}
                            dateFormat="dd/MM/yyyy HH:mm"
                            locale="el"
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            className="form-control rounded-3 border-light shadow-sm"
                            placeholderText="Επιλέξτε αρχική ημερομηνία"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-semibold text-secondary mb-2">Έως Ημερομηνία & Ώρα</Form.Label>
                          <DatePicker
                            selected={endDate}
                            onChange={(d) => setEndDate(d)}
                            showTimeInput
                            customTimeInput={<CustomTimeInput />}
                            dateFormat="dd/MM/yyyy HH:mm"
                            locale="el"
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            className="form-control rounded-3 border-light shadow-sm"
                            placeholderText="Επιλέξτε τελική ημερομηνία"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>

            {selectedDevices.length > 0 ? (
              <Card className="shadow-sm border-0 rounded-4">
                <Card.Body className="p-4 bg-white">
                  <TemperatureChart
                    deviceIds={selectedDevices}
                    hours={isCustom ? 24 : timeRange}
                    devices={devices}
                    startDate={isCustom ? startDate : null}
                    endDate={isCustom ? endDate : null}
                  />
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="info" className="shadow-sm border-0 rounded-4 p-4">Επιλέξτε συσκευή από τη λίστα για να δείτε το γράφημα.</Alert>
            )}
          </>
        )}

        {/* EXPORT MODAL */}
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="lg" centered>
          <Modal.Header closeButton className="border-bottom-0"><Modal.Title className="fw-bold">Εξαγωγή Δεδομένων</Modal.Title></Modal.Header>
          <Modal.Body className="px-4">
            <Form>
            <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-muted small">Εξαγωγή για:</Form.Label>
                <Form.Select 
                  className="rounded-3 border-light shadow-sm bg-white text-dark"
                  value={exportDeviceId} 
                  onChange={(e) => setExportDeviceId(e.target.value)}
                >
                  <option value="selected">Όλα τα επιλεγμένα γραφήματος ({selectedDevices.length})</option>
                  {devices.filter(d => selectedDevices.includes(d.id)).map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold text-muted small">Από</Form.Label>
                    <DatePicker
                      selected={exportStartDate}
                      onChange={(d) => { setExportStartDate(d); }}
                      showTimeInput
                      customTimeInput={<CustomTimeInput />}
                      dateFormat="dd/MM/yyyy HH:mm"
                      locale="el"
                      selectsStart
                      startDate={exportStartDate}
                      endDate={exportEndDate}
                      className="form-control rounded-3 border-light shadow-sm"
                      placeholderText="Επιλέξτε ημερομηνία και ώρα"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold text-muted small">Έως</Form.Label>
                    <DatePicker
                      selected={exportEndDate}
                      onChange={(d) => { setExportEndDate(d); }}
                      showTimeInput
                      customTimeInput={<CustomTimeInput />}
                      dateFormat="dd/MM/yyyy HH:mm"
                      locale="el"
                      selectsEnd
                      startDate={exportStartDate}
                      endDate={exportEndDate}
                      className="form-control rounded-3 border-light shadow-sm"
                      placeholderText="Επιλέξτε ημερομηνία και ώρα"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex gap-2 mb-2">
                <Button size="sm" variant="outline-secondary" className="rounded-pill px-3" onClick={() => setQuickRange(6)}>6h</Button>
                <Button size="sm" variant="outline-secondary" className="rounded-pill px-3" onClick={() => setQuickRange(24)}>24h</Button>
                <Button size="sm" variant="outline-secondary" className="rounded-pill px-3" onClick={() => setQuickRange(168)}>7d</Button>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-top-0 px-4 pb-4">
            <Button variant="light" className="rounded-3 fw-bold text-muted" onClick={() => setShowExportModal(false)}>Ακύρωση</Button>
            <Button variant="danger" className="rounded-3 shadow-sm px-4 fw-bold" onClick={() => handleExportWithDates('pdf')}>PDF</Button>
            <Button variant="success" className="rounded-3 shadow-sm px-4 fw-bold" onClick={() => handleExportWithDates('excel')}>Excel</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default Dashboard;